import { ResponseError } from '@backstage/errors';
import {
  ApprovalStateResponse,
  AuthorizeRequest,
  AuthorizeResponse,
  CapabilitiesResponse,
  OperationsResponse,
  OperationSummary,
  OpsReadRequest,
  OpsReadResult,
  WhoAmIResponse,
} from './types';

/**
 * Typed client for the opsicle-poc backend.
 *
 * One method per route, mirroring the backend's route set. There is
 * deliberately no generic `call(path, body)` escape hatch: the whole design
 * rests on the route set being a reviewable, enumerable bound, and a generic
 * method would let the frontend reach a route nobody declared.
 */
export interface OpsiclePocApi {
  getCapabilities(): Promise<CapabilitiesResponse>;
  whoAmI(): Promise<WhoAmIResponse>;
  authorize(request: AuthorizeRequest): Promise<AuthorizeResponse>;
  /** Every operation this plugin can dispatch, with the tier each carries. */
  getOperations(): Promise<OperationsResponse>;
  runOperation(
    operation: OperationSummary,
    request: OpsReadRequest,
  ): Promise<OpsReadResult>;
  approvalState(
    capability: string,
    shard?: string,
  ): Promise<ApprovalStateResponse>;
}

export class OpsiclePocApiClient implements OpsiclePocApi {
  private readonly discoveryApi: {
    getBaseUrl(pluginId: string): Promise<string>;
  };
  private readonly fetchApi: { fetch: typeof fetch };

  constructor(options: {
    discoveryApi: { getBaseUrl(pluginId: string): Promise<string> };
    fetchApi?: { fetch: typeof fetch };
  }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi ?? { fetch: fetch.bind(window) };
  }

  private baseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('opsicle-poc');
  }

  async getCapabilities(): Promise<CapabilitiesResponse> {
    const response = await this.fetchApi.fetch(
      `${await this.baseUrl()}/capabilities`,
    );

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }

  /** The caller's own identity and the groups the gate resolves for them. */
  async whoAmI(): Promise<WhoAmIResponse> {
    const response = await this.fetchApi.fetch(
      `${await this.baseUrl()}/whoami`,
    );

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }

  /**
   * The caller's own approval state. Takes no subject: the backend answers
   * only about the authenticated caller, so there is nothing to pass.
   */
  async approvalState(
    capability: string,
    shard?: string,
  ): Promise<ApprovalStateResponse> {
    const params = new URLSearchParams({ capability });
    if (shard) {
      params.set('shard', shard);
    }

    const response = await this.fetchApi.fetch(
      `${await this.baseUrl()}/approval-state?${params.toString()}`,
    );

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }

  async getOperations(): Promise<OperationsResponse> {
    const response = await this.fetchApi.fetch(
      `${await this.baseUrl()}/operations`,
    );

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }

  async authorize(request: AuthorizeRequest): Promise<AuthorizeResponse> {
    const response = await this.fetchApi.fetch(
      `${await this.baseUrl()}/authorize`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      },
    );

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }

  /**
   * 🔴 This method does NOT throw on a non-2xx response, and that is the point.
   *
   * Every failure shape this route produces is a distinct, meaningful result:
   * the gate refused (403), the gate allowed and no executor is configured
   * (501), the caller addressed a shard this instance does not serve (400),
   * the executor timed out (504) or was unreachable (502), or the executor
   * answered and its status is passed through verbatim — including a 401 or
   * 403 from its own capability guard.
   *
   * Throwing would flatten all of those into one error, and the single most
   * important distinction in this whole POC is "the gate denied you" versus
   * "the gate allowed you and the executor is down".
   */
  async runOperation(
    operation: OperationSummary,
    request: OpsReadRequest,
  ): Promise<OpsReadResult> {
    // The route comes from the operation, not from a literal here: the
    // backend generates one gated route per registry entry, so following the
    // registry is what keeps the client from calling a path nobody declared.
    const response = await this.fetchApi.fetch(
      `${await this.baseUrl()}${operation.route}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shard: request.shard,
          workspaceId: request.workspaceId,
          // The executor's DTO names this field, and rejects anything else.
          ...(request.ids ? { [operation.idsField]: request.ids } : {}),
        }),
      },
    );

    const raw = await response.text();
    const parsed = safeJson(raw);

    if (response.ok) {
      return {
        kind: 'executorResponded',
        status: response.status,
        ok: true,
        body: raw,
      };
    }

    // 🔴 Framework rejection first, before anything reads the status code.
    //
    // Backstage answers an unverifiable token with 401 and its own error
    // envelope, and the executor's capability guard answers a bad validation
    // token with a bare 401. Same status, completely different systems: one
    // means "your session expired", the other means gate 5 did its job. This
    // was caught by a live session expiring mid-test, where the UI reported
    // "the executor's guard refused" for a request that never left Backstage.
    const envelope = frameworkError(parsed);
    if (envelope && (response.status === 401 || response.status === 403)) {
      return {
        kind: 'callerRejected',
        status: response.status,
        name: envelope.name,
        message: envelope.message,
      };
    }

    switch (response.status) {
      case 403:
        // Our gate's denial body is `{error: 'forbidden', reason}`. An executor
        // that answers 403 is passed through with its own body, so the shape —
        // not the status — is what separates them.
        if (
          parsed?.error === 'forbidden' &&
          typeof parsed?.reason === 'string'
        ) {
          return {
            kind: 'gateDenied',
            status: 403,
            reason: parsed.reason,
            detail:
              typeof parsed.detail === 'string' ? parsed.detail : undefined,
          };
        }
        break;
      case 501:
        if (parsed?.status === 'notConfigured') {
          return {
            kind: 'executorNotConfigured',
            status: 501,
            note: String(parsed.note ?? ''),
            decision: String(parsed.decision ?? ''),
            reason: String(parsed.reason ?? ''),
          };
        }
        // Planned upstream is a gate outcome: the executor was not called.
        // Leaving this to the fallthrough below reports it as an executor answer.
        if (
          parsed?.error === 'notImplemented' &&
          parsed.reason === 'upstream-planned'
        ) {
          return {
            kind: 'upstreamPlanned',
            status: 501,
            reason: 'upstream-planned',
            detail: String(parsed.detail ?? ''),
          };
        }
        break;
      case 400:
        if (parsed?.error === 'shardMismatch') {
          return {
            kind: 'shardMismatch',
            status: 400,
            requested: String(parsed.requested ?? ''),
            served: String(parsed.served ?? ''),
          };
        }
        // A Backstage-side framework rejection (error is an object) never
        // reached the executor, so the gate box should read "not reached".
        if (frameworkError(parsed)) {
          return {
            kind: 'badRequest',
            status: 400,
            message: frameworkError(parsed)?.message ?? raw,
          };
        }
        // Otherwise the executor itself answered 400 — e.g. its own DTO
        // validation rejecting a non-UUID id. Gate 5 verified the token before
        // that ran, so the gate WAS reached and passed: fall through to the
        // executor-responded path (gate ALLOWED) and keep the body so the
        // reason (its NestJS `message`) can be rendered.
        break;
      case 504:
        if (parsed?.error === 'agentTimeout') {
          return {
            kind: 'executorTimeout',
            status: 504,
            shard: String(parsed.shard ?? ''),
            url: String(parsed.url ?? ''),
          };
        }
        break;
      case 502:
        if (parsed?.error === 'agentUnreachable') {
          return {
            kind: 'executorUnreachable',
            status: 502,
            shard: String(parsed.shard ?? ''),
            url: String(parsed.url ?? ''),
          };
        }
        break;
      default:
        break;
    }

    // Anything else is the executor answering for itself. Keep the status and
    // the body intact rather than inventing a category for it.
    return {
      kind: 'executorResponded',
      status: response.status,
      ok: false,
      body: raw,
    };
  }
}

/**
 * Recognise Backstage's own error envelope: `{error: {name, message}, ...}`.
 *
 * Our gate's denial body is `{error: 'forbidden', reason}` — `error` is a
 * string there — so the shape separates the framework's rejections from ours
 * and from the executor's without guessing from the status code.
 */
function frameworkError(
  parsed?: Record<string, unknown>,
): { name: string; message: string } | undefined {
  const error = parsed?.error;
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }
  const { name, message } = error as { name?: unknown; message?: unknown };
  if (typeof name !== 'string') {
    return undefined;
  }
  return { name, message: typeof message === 'string' ? message : '' };
}

function safeJson(raw: string): Record<string, unknown> | undefined {
  try {
    const value = JSON.parse(raw);
    return typeof value === 'object' && value !== null
      ? (value as Record<string, unknown>)
      : undefined;
  } catch {
    return undefined;
  }
}
